import type { DataModelColumn, DataModelTable } from './types';

export function validateAndFixRelationships(
	tables: DataModelTable[],
): DataModelTable[] {
	const factTable = tables.find((t) => t.tableType === 'fact');
	const dimensionTables = tables.filter((t) => t.tableType === 'dimension');

	if (!factTable) {
		console.error(
			'No fact table found in the model. Please ask the user to try again with a different prompt.',
		);
		return tables;
	}

	const allForeignKeysHaveValidReferences = factTable.columns.every((col) => {
		if (!col.isForeignKey) return true;
		if (!col.referenceTo) return false;

		const referencedTable = dimensionTables.find(
			(dim) => dim.tableName === col.referenceTo?.table,
		);
		if (!referencedTable) return false;

		const pkColumn = referencedTable.columns.find(
			(pkCol) =>
				pkCol.isPrimaryKey &&
				pkCol.columnName === col.referenceTo?.column,
		);
		return !!pkColumn;
	});

	if (allForeignKeysHaveValidReferences) {
		return tables;
	}

	const dimensionPrimaryKeys = new Map<
		string,
		{ tableName: string; columnName: string }
	>(
		dimensionTables
			.map((dim) => {
				const pkColumn = dim.columns.find((col) => col.isPrimaryKey);
				return pkColumn
					? ([
							pkColumn.columnName.toLowerCase(),
							{
								tableName: dim.tableName,
								columnName: pkColumn.columnName,
							},
					  ] as const)
					: null;
			})
			.filter(
				(
					entry,
				): entry is [
					string,
					{ tableName: string; columnName: string },
				] => entry !== null,
			),
	);

	const modifiedTables = [...tables];
	const factTableIndex = modifiedTables.findIndex(
		(t) => t.tableType === 'fact',
	);

	modifiedTables[factTableIndex] = {
		...factTable,
		columns: factTable.columns.map((col) => {
			if (!col.isForeignKey) return col;

			const pkInfo = dimensionPrimaryKeys.get(
				col.columnName.toLowerCase(),
			);

			if (pkInfo) {
				if (
					!col.referenceTo ||
					col.referenceTo.table !== pkInfo.tableName ||
					col.referenceTo.column !== pkInfo.columnName
				) {
					return {
						...col,
						referenceTo: {
							table: pkInfo.tableName,
							column: pkInfo.columnName,
						},
					} satisfies DataModelColumn;
				}
			}
			return col;
		}),
	};

	return modifiedTables;
}
